// React component to render load rates in invoice
import React, { useState } from 'react';
import TwAdvancedRates from '../../../components/TwAdvancedRates/TwAdvancedRates';
import {
  IExpandedItems,
  TwAccordion,
  TwAccordionDetails,
  TwAccordionSummary
} from '../../../components/TwAccordion/TwAccordion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NumberUtils from '../../../utils/numberUtils';
import useAuth from '../../../providers/Auth/Auth.provider';
import { InvoiceSources } from '../../../redux/api/invoiceService/invoiceService.model';
import { useGetTenantAccountingSettingsQuery } from '../../../redux/api/tenant/tenantApi';
import { Order } from '../../../redux/api/orders/orders.model';
import { RowData } from '../../../components/TwAdvancedRates/RateRow';

export interface LoadsRatesProps {
  loads: Order[];
  loadsRates: { [key: string]: RowData[] } | undefined;
  onUpdateLoadRates: (data: RowData[], loadId: string) => void;
  source?: InvoiceSources;
  divisionId: string;
  disabled?: boolean;
}

export const LoadsRates: React.FC<LoadsRatesProps> = ({
  loads,
  loadsRates,
  onUpdateLoadRates,
  source,
  divisionId,
  disabled
}) => {
  const { data: divisionAccountingSettings } = useGetTenantAccountingSettingsQuery(
    {
      divisionId: divisionId
    },
    { skip: !divisionId }
  );

  const [expandedItems, setExpandedItems] = useState<IExpandedItems>({});
  const truckbaseLoadNumber =
    divisionAccountingSettings?.invoice_description.show_truckbase_load_number;
  const customerOrderNumber =
    divisionAccountingSettings?.invoice_description.show_customer_load_number;

  const handleAccordionExpandedChange = (key: string): void => {
    const currentExpanded = expandedItems[key];
    setExpandedItems((currentExpandedItems) => ({
      ...currentExpandedItems,
      [key]: !currentExpanded
    }));
  };

  const getGrandTotal = (): string => {
    let sumInCents = 0;
    if (loadsRates) {
      Object.values(loadsRates).forEach((loadRate) => {
        loadRate.forEach((rate) => {
          if (rate.amount) {
            // First round each amount to 2 decimal places
            const roundedAmount = Math.round(Number(rate.amount) * 100) / 100;
            // Then convert to cents for summing
            sumInCents += Math.round(roundedAmount * 100);
          }
        });
      });
    }

    // Convert back to dollars
    const total = sumInCents / 100;
    return NumberUtils.formatNumber(total || 0, 2, 2);
  };

  return (
    <>
      {loads.map((load, index) => {
        return (
          <TwAccordion key={load.id} expanded={!!expandedItems[load.id]}>
            <TwAccordionSummary
              aria-controls={load.id}
              expandIcon={
                <ExpandMoreIcon onClick={(): void => handleAccordionExpandedChange(load.id)} />
              }
              onClick={(): void => handleAccordionExpandedChange(load.id)}
            >
              <Stack direction="row" spacing={1} width="100%" justifyContent="space-between">
                <Typography variant="body2" fontWeight="600" fontSize="14px" color="#1F2937">
                  {truckbaseLoadNumber && customerOrderNumber && load.customer_order_number
                    ? `Order: #${load.readable_id}, Customer order: #${load.customer_order_number}`
                    : truckbaseLoadNumber
                    ? `Order #${load.readable_id}`
                    : customerOrderNumber && load.customer_order_number
                    ? `Customer order #${load.customer_order_number}`
                    : `Order #${load.readable_id}`}
                </Typography>

                <Stack direction="row" spacing={0.5}>
                  <Typography variant="body2" fontWeight="600" fontSize="14px" color="#9CA3AF">
                    $
                  </Typography>
                  <Typography variant="body2" fontWeight="600" fontSize="14px" color="#1F2937">
                    {loadsRates &&
                      load.id &&
                      NumberUtils.formatNumber(
                        loadsRates[load.id]?.reduce((acc, row) => {
                          // Per invoicing rules:
                          // 1. First get the amount with full precision (up to 4 decimals)
                          const amountInTenThousandths = Math.round(
                            Number(row.amount || 0) * 10000
                          );
                          const amount = amountInTenThousandths / 10000;

                          // 2. Round to 2 decimal places before adding to total
                          const roundedAmount = Math.round(amount * 100) / 100;

                          // 3. Convert accumulator to 2 decimal places as well
                          const roundedAcc = Math.round(acc * 100) / 100;

                          // 4. Add the rounded values
                          return roundedAcc + roundedAmount;
                        }, 0) || 0,
                        2, // Display with 2 decimal places in UI
                        2
                      )}
                  </Typography>
                </Stack>
              </Stack>
            </TwAccordionSummary>
            <TwAccordionDetails>
              <TwAdvancedRates
                initialData={loadsRates && loadsRates[load.id]}
                showDescription={true}
                totalLoadedMiles={
                  load.estimated_mileage
                    ? NumberUtils.roundNumber(load.estimated_mileage, 2)
                    : undefined
                }
                onRatesUpdate={(data: RowData[]): void => {
                  onUpdateLoadRates(data, load.id);
                }}
                isImportLoad={false}
                source={source}
                disabled={disabled}
                selectedDivisionId={divisionId}
              />
            </TwAccordionDetails>
          </TwAccordion>
        );
      })}

      {/* Only showing grand total when is a multi load invoice */}
      {loads.length > 1 && (
        <Stack
          direction="row"
          spacing={1}
          width="100%"
          justifyContent="flex-end"
          padding="0px 16px"
        >
          <Typography variant="body2" fontWeight="600" fontSize="14px" color="#9CA3AF">
            GRAND TOTAL
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Typography variant="body2" fontWeight="600" fontSize="14px" color="#9CA3AF">
              $
            </Typography>
            <Typography variant="body2" fontWeight="600" fontSize="14px" color="#1F2937">
              {getGrandTotal()}
            </Typography>
          </Stack>
        </Stack>
      )}
    </>
  );
};
